import React, { useEffect, useRef, useState } from 'react'
import {
  Typography,
  makeStyles,
  Box
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Controller, useFormContext } from 'react-hook-form'
import { IntegrationDots, IntegrationDotsProps } from '../integrationDots'
import { useDefaultMenuItemStyles } from '../../../../hooks/defaultSelectStylesHook'
import { BootstrapInput } from '../../../../components/BootstrapInput'
import { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete/Autocomplete'
import clsx from 'clsx'
import { useFormStyles } from '../../lib/hooks/useFormStyles'

interface IOption {
  id: string

  name: string
}

interface FormAsyncAutocompleteProps extends IntegrationDotsProps {
  name: string

  label: string

  fetchOptions: (input: string) => Promise<IOption[]>

  required?: boolean

  placeholder?: string

  disabled?: boolean

  onSelected?: (category: IOption) => void
}

// TODO: Create AbortController system to avoid race condition

export const FormAsyncAutocomplete = ({
                                        name,
                                        label,
                                        fetchOptions,
                                        required,
                                        placeholder,
                                        disabled,
                                        types,
                                        onSelected
                                      }: FormAsyncAutocompleteProps) => {
  const formClasses = useFormStyles()
  const componentClasses = useStyles()
  const menuClasses = useDefaultMenuItemStyles()
  const {control} = useFormContext()

  const [options, setOptions] = useState<IOption[]>([])
  const [loading, setLoading] = useState(false)

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = useRef(true)

  const inputValueRef = useRef('')

  useEffect(() => {
    return () => {
      isMountedRef.current = false

      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
      }
    }
  }, [])

  const loadOptions = (input: string): void => {
    fetchOptions(input)
      .then((results) => {
        if (isMountedRef.current) {
          setOptions(results)
        }
      })
      .catch((error: unknown) => {
        console.error(error)
      })
      .finally(() => {
        if (isMountedRef.current) {
          setLoading(false)
        }
      })
  }

  const handleInputChange = (value: string) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current)
    }

    if (value.length >= 3) {
      setLoading(true)
      debounceTimeout.current = setTimeout(() => {
        loadOptions(value)
      }, 300)
    } else {
      setOptions([])
      setLoading(false)
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={null}
      rules={{required: required ? 'Это поле обязательно' : false}}
      render={({field, fieldState}) => (
        <Box>
          <Box display="flex" alignItems="center" className={formClasses.root}>
            <Typography variant="subtitle1" className={formClasses.label}>
              {label}
            </Typography>
            <IntegrationDots types={types}/>
          </Box>
          <Autocomplete
            {...field}
            className={clsx(componentClasses.autoComplete)}
            classes={
              {
                popper: componentClasses.popperSmall,
                option: menuClasses.menuItem,
                listbox: menuClasses.menu,
                noOptions: componentClasses.helperText,
                loading: componentClasses.helperText
              }
            }
            disabled={disabled}
            onChange={(_, data) => {
              field.onChange(data)
              if (data) {
                onSelected?.(data)
              }
            }}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') {
                inputValueRef.current = value
                handleInputChange(value)
              }
            }}
            options={options}
            getOptionLabel={(opt: IOption) => opt.name}
            getOptionSelected={(opt, val) => opt.id === val.id}
            noOptionsText={
              inputValueRef.current.length < 3
              ? 'Введите не менее 3 символов'
              : 'Нет доступных вариантов'
            }
            loading={loading}
            renderInput={(props: AutocompleteRenderInputParams) => {
              const {InputProps, InputLabelProps /* ignored */, ...passProps} = props

              return (
                <BootstrapInput
                  {...passProps}
                  {...InputProps}
                  placeholder={placeholder}
                  className={formClasses.autoselect}
                  autoFocus
                />
              )
            }}
          />
        </Box>
      )}
    />
  )
}

const useStyles =
  makeStyles(() => ({
    autoComplete: {
      minWidth: 300,
      padding: 0
    },
    popperSmall: {
      fontSize: 10
    },
    helperText: {
      fontSize: 12
    }
  }))